import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Building2, CheckCircle2, ChevronRight, HandHelping, Loader2, LogOut, MapPin, Navigation, Shield, Sparkles, UserRound } from 'lucide-react';
import { API } from '@/lib/helpers';
import { clearRememberedAccess, getAccessOptions, getRememberedAccess, rememberAccess } from '@/lib/accessOptions';

const iconMap = {
  gestor: Shield,
  admin: Building2,
  auxiliar: HandHelping,
  ebi: Sparkles,
  movimentos: Navigation,
};

const accentMap = {
  gestor: { iconBg: 'bg-blue-50', iconText: 'text-blue-600', text: 'text-slate-900' },
  admin: { iconBg: 'bg-blue-50', iconText: 'text-blue-600', text: 'text-slate-900' },
  auxiliar: { iconBg: 'bg-blue-50', iconText: 'text-blue-600', text: 'text-slate-900' },
  ebi: { iconBg: 'bg-blue-50', iconText: 'text-blue-600', text: 'text-slate-900' },
  movimentos: { iconBg: 'bg-blue-50', iconText: 'text-blue-600', text: 'text-slate-900' },
};

export default function AccessSelector() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rememberLast, setRememberLast] = useState(false);

  const options = useMemo(() => getAccessOptions(user), [user]);

  useEffect(() => {
    fetch(`${API}/auth/me`, { credentials: 'include' })
      .then(async res => {
        if (!res.ok) throw new Error('auth');
        return res.json();
      })
      .then(data => {
        if (data.must_change_password) {
          navigate('/alterar-senha', { replace: true });
          return;
        }
        const available = getAccessOptions(data);
        const remembered = getRememberedAccess(available);
        const forceChoice = new URLSearchParams(location.search).get('trocar') === '1';
        if (available.length === 1 && !forceChoice) {
          navigate(available[0].route, { replace: true });
          return;
        }
        if (remembered && !forceChoice) {
          navigate(remembered.route, { replace: true });
          return;
        }
        setUser(data);
      })
      .catch(() => navigate('/', { replace: true }))
      .finally(() => setLoading(false));
  }, [location.search, navigate]);

  const chooseAccess = (option) => {
    if (rememberLast) rememberAccess(option);
    else clearRememberedAccess();
    sessionStorage.setItem('skip_onboarding_once', '1');
    navigate(option.route, { replace: true });
  };

  const logout = async () => {
    clearRememberedAccess();
    await fetch(`${API}/auth/logout`, { method: 'POST', credentials: 'include' }).catch(() => {});
    navigate('/', { replace: true });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center text-slate-500" data-testid="access-selector-loading">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Carregando acessos...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-5 sm:px-8 sm:py-8" data-testid="access-selector-page">
      <div className="pointer-events-none fixed inset-0 overflow-hidden" aria-hidden="true">
        <div className="absolute left-0 top-0 h-44 w-full bg-gradient-to-b from-white to-transparent" />
        <div className="absolute right-[-12rem] top-[-12rem] h-96 w-96 rounded-full bg-blue-100/60 blur-3xl" />
        <div className="absolute bottom-[-14rem] left-[-10rem] h-96 w-96 rounded-full bg-emerald-100/60 blur-3xl" />
      </div>

      <div className="relative mx-auto flex min-h-[calc(100vh-4rem)] w-full max-w-5xl flex-col">
        <header className="flex items-center justify-between gap-3" data-testid="access-selector-header">
          <div className="flex items-center gap-3 rounded-full border border-slate-200 bg-white/85 px-3 py-2 shadow-sm backdrop-blur-sm" data-testid="access-selector-user-badge">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-900 text-white">
              <UserRound className="h-4 w-4" />
            </div>
            <div className="hidden min-w-0 sm:block">
              <p className="truncate text-sm font-semibold text-slate-900">{user?.name || 'Usuário'}</p>
              <p className="truncate text-xs text-slate-500">{user?.email || 'acesso autenticado'}</p>
            </div>
          </div>
          <Button type="button" variant="outline" onClick={logout} className="rounded-full border-slate-200 bg-white/85 text-slate-600 hover:bg-red-50 hover:text-red-600" data-testid="access-selector-logout-button">
            <LogOut className="mr-2 h-4 w-4" /> Sair
          </Button>
        </header>

        <main className="flex flex-1 items-center justify-center py-8 sm:py-12">
          <Card className="w-full max-w-2xl rounded-3xl border border-slate-100 bg-white p-5 shadow-xl shadow-slate-200/60 sm:p-8 md:p-10" data-testid="access-selector-card">
            <section className="mb-7" data-testid="access-selector-hero">
              <Badge variant="outline" className="mb-4 rounded-full border-blue-100 bg-blue-50 text-blue-700" data-testid="access-selector-count-badge">
                <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" /> {options.length} acessos disponíveis
              </Badge>
              <h1 className="text-3xl font-semibold tracking-normal text-slate-950 sm:text-4xl" style={{ fontFamily: 'Work Sans, sans-serif' }} data-testid="access-selector-title">
                Escolha seu contexto
              </h1>
              <p className="mt-3 max-w-xl text-base leading-relaxed text-slate-500" data-testid="access-selector-subtitle">
                Selecione o painel que deseja abrir agora. Você poderá alternar novamente pelo menu do sistema.
              </p>
              {user?.church_name && (
                <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-sm text-slate-600" data-testid="access-selector-church">
                  <MapPin className="h-4 w-4 text-slate-400" /> {user.church_name}
                </div>
              )}
            </section>

            <section className="flex max-h-[50vh] flex-col gap-6 overflow-y-auto pr-1" data-testid="access-selector-options">
            {options.map((option, index) => {
              const Icon = iconMap[option.key] || Building2;
              const accent = accentMap[option.key] || accentMap.admin;
              return (
                <Card
                  key={option.key}
                  role="button"
                  tabIndex={0}
                  onClick={() => chooseAccess(option)}
                  onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') chooseAccess(option); }}
                  aria-label={`Acessar ${option.label}`}
                  style={{ animationDelay: `${index * 35}ms` }}
                  className="group cursor-pointer rounded-2xl border border-slate-200 bg-white p-4 transition-[border-color,background-color] duration-200 hover:border-blue-200 hover:bg-blue-50/30 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 sm:p-5"
                  data-testid={`access-option-${option.key}`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${accent.iconBg}`} data-testid={`access-option-${option.key}-icon`}>
                      <Icon className={`h-6 w-6 ${accent.iconText}`} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h2 className={`text-base font-bold sm:text-lg ${accent.text}`} data-testid={`access-option-${option.key}-title`}>{option.label}</h2>
                      <p className="mt-1 text-sm text-slate-600" data-testid={`access-option-${option.key}-description`}>{option.description}</p>
                    </div>
                    <ChevronRight className="h-5 w-5 text-slate-400 transition-colors duration-200 group-hover:text-blue-500" />
                  </div>
                </Card>
              );
            })}
            </section>

            <div className="mt-6 border-t border-slate-100 pt-5">
              <label className="flex cursor-pointer items-center gap-3 text-sm font-medium text-slate-600" data-testid="access-selector-remember-label">
              <input type="checkbox" checked={rememberLast} onChange={(event) => setRememberLast(event.target.checked)} className="h-5 w-5 rounded border-slate-300 text-slate-900 focus:ring-slate-900" data-testid="access-selector-remember-checkbox" />
              Lembrar minha escolha neste navegador
            </label>
            </div>
          </Card>
        </main>
      </div>
    </div>
  );
}
